<template>
  <div class="container-nzidoo">

    <page-title title="sentence.myCart" />

    <!-- Search Input -->
    <div class="admin-toolbar">
      <div class="admin-search-box">
        <i class="mdi mdi-magnify admin-search-icon"></i>
        <input type="text" v-model="searchTerm" :placeholder="$t('sentence.searchByName')"
          class="admin-search-input">
        <i v-if="searchTerm" class="mdi mdi-close-circle admin-clear-icon" @click="clearSearch"
          :title="$t('sentence.clear')"></i>
      </div>
      <button v-if="cartItems.length > 0" class="btn btn-danger btn-sm" @click="clearCart">
        <i class="mdi mdi-delete-sweep"></i>
        {{ $t('sentence.clearCart') }}
      </button>
    </div>

    <loading-spinner v-if="loading" />

    <!-- Empty Cart -->
    <div v-else-if="cartItems.length === 0" class="text-center text-muted py-5">
      <i class="mdi mdi-cart-outline fa-3x mb-3"></i>
      <p class="px-3">{{ $t('sentence.cartIsEmpty') }}</p>
      <button class="btn btn-primary mt-3" @click="$router.push('/')">
        {{ $t('sentence.continueShopping') }}
      </button>
    </div>

    <!-- No search results -->
    <div v-else-if="filteredCartItems.length === 0" class="admin-empty-state">
      <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="11" cy="11" r="8"></circle>
        <path d="m21 21-4.35-4.35"></path>
      </svg>
      <p class="admin-empty-text">{{ $t('sentence.noResults') }}</p>
    </div>

    <!-- Cart Items -->
    <div v-else class="cart-container">
      <div class="row g-4">
        <!-- Cart Items List -->
        <div class="col-lg-8">
          <div class="cart-items-wrapper">
            <div v-for="item in filteredCartItems" :key="item.id" class="cart-item">
              <div class="cart-item-image" @click="moveToProductPage(item.product.id)">
                <img v-if="item.product.pictures && item.product.pictures.length > 0"
                  :src="`${storageUrl}/${JSON.parse(item.product.pictures)[0]}`" :alt="item.product.name" />
                <div v-else class="no-image">
                  <i class="mdi mdi-image-off"></i>
                </div>
              </div>

              <div class="cart-item-details">
                <h5 class="cart-item-title" @click="moveToProductPage(item.product.id)">
                  {{ item.product.name }}
                </h5>
                <p class="cart-item-seller">
                  <i class="mdi mdi-store"></i>
                  {{ item.product.seller.username }}
                </p>

                <!-- Selected Attributes -->
                <div v-if="item.selected_attributes && Object.keys(item.selected_attributes).length > 0"
                  class="cart-item-attributes">
                  <span v-for="(value, key) in item.selected_attributes" :key="key" class="attribute-badge">
                    {{ key }}: {{ value }}
                  </span>
                </div>

                <!-- Quantity Controls -->
                <div class="quantity-controls">
                  <button class="qty-btn" @click="updateQuantity(item, item.quantity - 1)"
                    :disabled="item.quantity <= 1 || updatingItem === item.id">
                    <i class="mdi mdi-minus"></i>
                  </button>
                  <input type="number" class="qty-input" v-model.number="item.quantity" @blur="validateQuantity(item)"
                    @keyup.enter="validateQuantity(item)" min="1" :max="item.product.quantity"
                    :disabled="updatingItem === item.id" />
                  <button class="qty-btn" @click="updateQuantity(item, item.quantity + 1)"
                    :disabled="item.quantity >= item.product.quantity || updatingItem === item.id">
                    <i class="mdi mdi-plus"></i>
                  </button>
                </div>

                <!-- Stock Status -->
                <p v-if="item.product.quantity < 5" class="stock-warning">
                  <i class="mdi mdi-alert"></i>
                  {{ $t('sentence.onlyXLeft', { count: item.product.quantity }) }}
                </p>
              </div>

              <div class="cart-item-actions">
                <div class="cart-item-price">
                  <span class="price-label">{{ $t('sentence.price') }}:</span>
                  <span class="price-value">{{ formatPrice(item.price) }}</span>
                </div>
                <div class="cart-item-subtotal">
                  <span class="subtotal-label">{{ $t('sentence.subtotal') }}:</span>
                  <span class="subtotal-value">{{ formatPrice(item.subtotal) }}</span>
                </div>
                <button class="btn-remove" @click="removeItem(item.id)" :disabled="removingItem === item.id"
                  :title="$t('action.delete')">
                  <i class="mdi mdi-trash-can"></i>
                  {{ $t('action.remove') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Cart Summary -->
        <div class="col-lg-4">
          <div class="cart-summary">
            <h4 class="summary-title">{{ $t('sentence.orderSummary') }}</h4>

            <div class="summary-row">
              <span>{{ $t('sentence.items') }} ({{ cartSummary.total_quantity }})</span>
              <span>{{ formatPrice(cartSummary.subtotal) }}</span>
            </div>

            <div class="summary-row">
              <span>{{ $t('sentence.shipping') }}</span>
              <span class="text-success">{{ $t('sentence.calculatedAtCheckout') }}</span>
            </div>

            <hr />

            <div class="summary-row summary-total">
              <span>{{ $t('sentence.total') }}</span>
              <span class="total-amount">{{ formatPrice(cartSummary.total) }}</span>
            </div>

            <button class="btn btn-primary btn-checkout w-100 mt-3" @click="proceedToCheckout"
              :disabled="cartItems.length === 0">
              <i class="mdi mdi-cart-check"></i>
              {{ $t('sentence.proceedToCheckout') }}
            </button>

            <button class="btn btn-outline-secondary w-100 mt-2" @click="$router.push('/')">
              <i class="mdi mdi-arrow-left"></i>
              {{ $t('sentence.continueShopping') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import config from "@/config.js";
import PageTitle from '@/components/templates/PageTitle.vue';
import LoadingSpinner from '@/components/templates/LoadingSpinner.vue';

export default {
  name: "Cart",
  components: {
    PageTitle,
    LoadingSpinner,
  },
  data() {
    return {
      cartItems: [],
      cartSummary: {
        total_items: 0,
        total_quantity: 0,
        subtotal: 0,
        total: 0,
      },
      loading: true,
      storageUrl: config.storageUrl,
      searchTerm: '',
      updatingItem: null,
      removingItem: null,
    };
  },
  computed: {
    filteredCartItems() {
      if (!this.searchTerm.trim()) {
        return this.cartItems;
      }

      const query = this.searchTerm.toLowerCase().trim();
      return this.cartItems.filter(item => {
        const productName = item.product?.name?.toLowerCase() || '';
        const sellerName = item.product?.seller?.username?.toLowerCase() || '';

        return productName.includes(query) || sellerName.includes(query);
      });
    }
  },
  async mounted() {
    await this.loadCart();
  },
  methods: {
    async loadCart() {
      try {
        const user = this.$store?.state?.user;
        if (!user) {
          this.$router.push("/login");
          return;
        }

        const res = await this.$axios.get("/cart");

        if (res.data.success) {
          this.cartItems = res.data.data.items || [];
          this.cartSummary = res.data.data.summary || {
            total_items: 0,
            total_quantity: 0,
            subtotal: 0,
            total: 0,
          };
        }

        // Update cart count in store
        this.$store.commit('setCartCount', this.cartSummary.total_quantity);

      } catch (error) {
        console.error("Failed to load cart:", error);
        this.$toast?.error(this.$t('sentence.failedToLoadCart'));
      } finally {
        this.loading = false;
      }
    },

    async updateQuantity(item, newQuantity) {
      if (newQuantity < 1 || newQuantity > item.product.quantity) {
        return;
      }

      this.updatingItem = item.id;

      try {
        const res = await this.$axios.put(`/cart/${item.id}`, {
          quantity: newQuantity
        });

        if (res.data.success) {
          // Update local item
          const index = this.cartItems.findIndex(i => i.id === item.id);
          if (index !== -1) {
            this.cartItems[index] = res.data.data;
          }

          // Recalculate summary
          this.recalculateSummary();

          this.$toast?.success(this.$t('sentence.quantityUpdated'));
        }
      } catch (error) {
        console.error("Failed to update quantity:", error);
        this.$toast?.error(this.$t('sentence.failedToUpdateQuantity'));
        // Reload cart on error
        await this.loadCart();
      } finally {
        this.updatingItem = null;
      }
    },

    async validateQuantity(item) {
      const quantity = parseInt(item.quantity);

      if (isNaN(quantity) || quantity < 1) {
        item.quantity = 1;
      } else if (quantity > item.product.quantity) {
        item.quantity = item.product.quantity;
        this.$toast?.warning(this.$t('sentence.maxQuantityReached'));
      }

      await this.updateQuantity(item, item.quantity);
    },

    async removeItem(itemId) {
      if (!confirm(this.$t('sentence.confirmRemoveFromCart'))) {
        return;
      }

      this.removingItem = itemId;

      try {
        const res = await this.$axios.delete(`/cart/${itemId}`);

        if (res.data.success) {
          // Remove item from local array
          this.cartItems = this.cartItems.filter(i => i.id !== itemId);

          // Recalculate summary
          this.recalculateSummary();

          this.$toast?.success(this.$t('sentence.itemRemoved'));
        }
      } catch (error) {
        console.error("Failed to remove item:", error);
        this.$toast?.error(this.$t('sentence.failedToRemoveItem'));
        // Reload on error
        await this.loadCart();
      } finally {
        this.removingItem = null;
      }
    },

    async clearCart() {
      if (!confirm(this.$t('sentence.confirmClearCart'))) {
        return;
      }

      try {
        const res = await this.$axios.delete('/cart');

        if (res.data.success) {
          this.cartItems = [];
          this.cartSummary = {
            total_items: 0,
            total_quantity: 0,
            subtotal: 0,
            total: 0,
          };

          this.$store.commit('setCartCount', 0);
          this.$toast?.success(this.$t('sentence.cartCleared'));
        }
      } catch (error) {
        console.error("Failed to clear cart:", error);
        this.$toast?.error(this.$t('sentence.failedToClearCart'));
      }
    },

    recalculateSummary() {
      this.cartSummary = {
        total_items: this.cartItems.length,
        total_quantity: this.cartItems.reduce((sum, item) => sum + item.quantity, 0),
        subtotal: this.cartItems.reduce((sum, item) => sum + item.subtotal, 0),
        total: this.cartItems.reduce((sum, item) => sum + item.subtotal, 0),
      };

      this.$store.commit('setCartCount', this.cartSummary.total_quantity);
    },

    moveToProductPage(id) {
      this.$router.push({ name: "productDetails", params: { id } });
    },

    proceedToCheckout() {
      // Navigate to checkout page
      this.$router.push({ name: "Checkout" });
    },

    formatPrice(price) {
      return new Intl.NumberFormat('fr-DZ', {
        style: 'currency',
        currency: 'DZD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(price || 0);
    },

    clearSearch() {
      this.searchTerm = '';
    }
  }
};
</script>

<style scoped src="@/assets/css/shared-admin-styles.css"></style>
<style scoped>
.cart-container {
  margin-top: 20px;
}

/* Cart Items Wrapper */
.cart-items-wrapper {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* Cart Item */
.cart-item {
  display: flex;
  gap: 20px;
  padding: 20px;
  border-radius: 12px;
  background: #f8f9fa;
  margin-bottom: 15px;
  transition: all 0.3s ease;
}

.cart-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.cart-item-image {
  width: 120px;
  height: 120px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  background: white;
}

.cart-item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.cart-item-image:hover img {
  transform: scale(1.05);
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e9ecef;
  color: #6c757d;
}

.no-image i {
  font-size: 40px;
}

/* Cart Item Details */
.cart-item-details {
  flex: 1;
  min-width: 0;
}

.cart-item-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
  cursor: pointer;
  color: #212529;
  transition: color 0.3s ease;
}

.cart-item-title:hover {
  color: var(--primary-color, #007bff);
}

.cart-item-seller {
  font-size: 14px;
  color: #6c757d;
  margin: 0 0 12px 0;
}

.cart-item-seller i {
  margin-right: 5px;
}

/* Attributes */
.cart-item-attributes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.attribute-badge {
  display: inline-block;
  padding: 4px 12px;
  background: white;
  border-radius: 20px;
  font-size: 12px;
  color: #495057;
  border: 1px solid #dee2e6;
}

/* Quantity Controls */
.quantity-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.qty-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.qty-btn:hover:not(:disabled) {
  background: var(--primary-color, #007bff);
  color: white;
  border-color: var(--primary-color, #007bff);
}

.qty-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.qty-input {
  width: 60px;
  height: 32px;
  text-align: center;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
}

.qty-input:focus {
  outline: none;
  border-color: var(--primary-color, #007bff);
}

.stock-warning {
  font-size: 12px;
  color: #dc3545;
  margin: 0;
}

.stock-warning i {
  margin-right: 4px;
}

/* Cart Item Actions */
.cart-item-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  text-align: right;
}

.cart-item-price,
.cart-item-subtotal {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.price-label,
.subtotal-label {
  font-size: 12px;
  color: #6c757d;
}

.price-value {
  font-size: 14px;
  font-weight: 600;
  color: #212529;
}

.subtotal-value {
  font-size: 16px;
  font-weight: 700;
  color: var(--primary-color, #007bff);
}

.btn-remove {
  padding: 6px 12px;
  background: transparent;
  border: 1px solid #dc3545;
  color: #dc3545;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.btn-remove:hover:not(:disabled) {
  background: #dc3545;
  color: white;
}

.btn-remove:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Cart Summary */
.cart-summary {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 20px;
}

.summary-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 20px 0;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
}

.summary-total {
  font-size: 18px;
  font-weight: 700;
  color: #212529;
}

.total-amount {
  color: var(--primary-color, #007bff);
  font-size: 24px;
}

.btn-checkout {
  padding: 12px;
  font-size: 16px;
  font-weight: 600;
}

/* Responsive */
@media (max-width: 991px) {
  .cart-summary {
    position: static;
    margin-top: 20px;
  }
}

@media (max-width: 768px) {
  .cart-item {
    flex-direction: column;
    gap: 15px;
  }

  .cart-item-image {
    width: 100%;
    height: 200px;
  }

  .cart-item-actions {
    align-items: flex-start;
    text-align: left;
  }

  .btn-remove {
    align-self: flex-start;
  }

  .admin-toolbar {
    flex-direction: column;
    gap: 10px;
  }
}

@media (max-width: 576px) {
  .cart-items-wrapper {
    padding: 10px;
  }

  .cart-item {
    padding: 15px;
  }

  .cart-item-title {
    font-size: 16px;
  }

  .cart-summary {
    padding: 16px;
  }

  .summary-title {
    font-size: 18px;
  }
}
</style>